import { useMemo } from 'react';
import {
  useFirebase,
  usePermissions as usePermissionsCore,
  usePermissionOperations,
  useUserOperations,
  useUserPermissionOverrideOperations,
  type UsePermissionsResult,
} from '@mesterplan/shared-firebase';
import { useCompany } from '@/contexts/CompanyContext';
import { useNetUser } from '@/hooks/useNetUser';
import { idpEnabled } from '@/lib/idp';
import { PermissionKey, UserMembershipModel, UserMembershipTypeModel } from '@mesterplan/shared-types';

const EMPTY_PERMISSIONS: readonly string[] = [];

/**
 * IDP / .NET permission source. `hasPermission` reads the server-resolved
 * effective set off `GET /api/users/me` (scoped by X-Active-Org — owner gets the
 * full set), so it stays consistent with the backend `RequirePermission` gates.
 * Role flags come from the active-org membership (CompanyContext.activeRole).
 * No dependency on a Firebase `currentUser` — that is null under IDP (#772).
 */
export function useNetPermissions(): UsePermissionsResult {
  const { user } = useNetUser();
  const { activeRole: role } = useCompany();

  const permissionList = user?.permissions ?? EMPTY_PERMISSIONS;

  // Sparse by design: only granted keys are present (`true`); absent keys read
  // as `undefined`, which hasPermission treats as false. No usePermissions
  // consumer reads `permissions` directly (all use hasPermission/isOwner/etc.),
  // so a full all-keys map would be wasted work.
  const permissions = useMemo(() => {
    const map = {} as Record<PermissionKey, boolean>;
    for (const key of permissionList) {
      map[key as PermissionKey] = true;
    }
    return map;
  }, [permissionList]);

  const hasPermission = useMemo(
    () => (permission: PermissionKey): boolean => permissions[permission] ?? false,
    [permissions],
  );

  return {
    hasPermission,
    permissions,
    isOwner: role === 'owner',
    isAdmin: role === 'admin' || role === 'owner',
    isUser: role === 'user',
    role,
    team: user?.team ?? null,
  };
}

/**
 * Legacy Firebase/Firestore permission source. Derives role + config from the
 * Firestore-shaped company membership and the Firebase Auth user.
 */
function useFirebasePermissions(): UsePermissionsResult {
  const { currentUser } = useFirebase();
  const { selectedCompany } = useCompany();

  // Fetch the current user's full user model to get their team
  const { useFetchUser } = useUserOperations();
  const { data: userData } = useFetchUser();

  // Fetch the current user's permission overrides from subcollection
  const { useFetchUserPermissionOverride } = useUserPermissionOverrideOperations({
    selectedCompany: selectedCompany ? { id: selectedCompany.id } : null,
  });
  const { data: userOverrideDoc } = useFetchUserPermissionOverride(currentUser?.uid);

  // Find the current user's membership in the selected company
  const userMembership = useMemo((): UserMembershipModel | null => {
    if (!currentUser || !selectedCompany) return null;
    return (
      selectedCompany.userMemberships?.find(
        (m) => m.uid === currentUser.uid
      ) ?? null
    );
  }, [currentUser, selectedCompany]);

  // Get the user's role
  const role: UserMembershipTypeModel | null = userMembership?.role ?? null;

  // Get the user's team (from user document)
  const team = userData?.team ?? null;

  // Get the company's permission configuration
  const permissionConfig = selectedCompany?.settings?.permissions ?? null;

  // Get the user's permission overrides (from subcollection, flat format)
  const userOverrides = userOverrideDoc?.permissions ?? null;

  // Use the core permission hook
  return usePermissionsCore({
    userId: currentUser?.uid,
    role,
    team,
    permissionConfig,
    userOverrides,
  });
}

/**
 * Web permission hook used by ProtectedRoute, the sidebar, pages and panels.
 *
 * Bound to one source at MODULE LOAD rather than branched inside the hook body.
 * This is deliberate and differs from the CompanyProvider/NetUserProvider
 * runtime branch (those return a different *component* — valid because component
 * identity may change between renders; a *hook* cannot, since
 * `idpEnabled ? useNetPermissions() : useFirebasePermissions()` would be a
 * conditional hook call and violate the rules of hooks). `idpEnabled` is a
 * build-time constant (`Boolean(import.meta.env.VITE_IDP_ISSUER_URL)`, fixed for
 * the bundle's lifetime — never a runtime toggle), so the chosen branch is
 * stable and each branch only ever runs the hooks valid in its mode (the .NET
 * source never touches Firestore; the Firebase source never calls the .NET
 * hooks). If `idpEnabled` ever became runtime-dynamic, this would need to move
 * to a wrapper-component boundary like CompanyProvider.
 *
 * @example
 * ```tsx
 * const { hasPermission, isOwner, isAdmin, team } = usePermissions();
 *
 * {hasPermission('projects.create') && <CreateProjectButton />}
 *
 * if (!hasPermission('settings.view')) return <Navigate to="/" />;
 * ```
 */
export const usePermissions: () => UsePermissionsResult = idpEnabled
  ? useNetPermissions
  : useFirebasePermissions;

/**
 * Web-specific wrapper for permission operations.
 * Provides CRUD operations for managing company permissions.
 *
 * Only owners can use these operations.
 *
 * @example
 * ```tsx
 * const { useUpdateSingleRolePermission, useResetRolePermissions } = usePermissionOps();
 *
 * const updatePermission = useUpdateSingleRolePermission();
 * const resetPermissions = useResetRolePermissions();
 *
 * // Toggle a permission
 * updatePermission.mutate({
 *   companyId: selectedCompany.id,
 *   role: 'admin',
 *   permission: 'settings.managePermissions',
 *   value: true,
 * });
 *
 * // Reset role to defaults
 * resetPermissions.mutate({
 *   companyId: selectedCompany.id,
 *   role: 'user',
 * });
 * ```
 */
export function usePermissionOps() {
  const { selectedCompany } = useCompany();

  return usePermissionOperations({
    selectedCompany: selectedCompany ? { id: selectedCompany.id } : null,
    notifications: {
      onSuccess: (message) => {
        // Web apps typically use a toast library
        // This will be handled by the component using the hook
        console.log('Permission operation success:', message);
      },
      onError: (message) => {
        console.error('Permission operation error:', message);
      },
    },
  });
}

/**
 * Hook to get a list of users with their roles for permission management UI.
 * Only useful for owners managing user-specific permissions.
 */
export function useCompanyUsers() {
  const { currentUser } = useFirebase();
  const { selectedCompany } = useCompany();

  const users = useMemo(() => {
    if (!selectedCompany?.userMemberships) return [];

    return selectedCompany.userMemberships.map((membership) => ({
      uid: membership.uid,
      role: membership.role,
      isCurrentUser: membership.uid === currentUser?.uid,
    }));
  }, [selectedCompany, currentUser]);

  return { users };
}

/**
 * Web-specific wrapper for user permission override operations.
 * Provides CRUD operations for managing user-specific permission overrides.
 *
 * Uses a subcollection pattern (COMPANIES/{id}/USER_PERMISSION_OVERRIDES/{userId})
 * for better cache invalidation and simpler data management.
 */
export function useUserPermissionOverrideOps() {
  const { selectedCompany } = useCompany();

  return useUserPermissionOverrideOperations({
    selectedCompany: selectedCompany ? { id: selectedCompany.id } : null,
    notifications: {
      onSuccess: (message: string) => {
        console.log('User permission override operation success:', message);
      },
      onError: (message: string) => {
        console.error('User permission override operation error:', message);
      },
    },
  });
}
